import { LayoutModule } from '@angular/cdk/layout';
import { ErrorHandler, enableProdMode, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { AppComponent } from './app/app.component';

import { environment } from './environments/environment';

// Your application services + interceptors
import { CustomErrorService } from './app/core/custom-error.service';
import { HttpInterceptor } from './app/core/http-interceptor';
import { API_BASE_URL, MceApiService } from './app/core/mceapi.client.generated';

// Firebase modular providers
import { provideFirebaseServices } from './app/core/firebase.providers';

import { FirebaseAnalyticsService } from './app/authentication/firebase.analytics.service';
import { Routes, provideRouter } from '@angular/router';
import { AdministrationComponent } from './app/administration/administration.component';
import { authGuard } from './app/authentication/auth-guard.service';
import { ProfileEditComponent } from './app/account/profile-edit/profile-edit.component';
import { SigninCompleteComponent } from './app/authentication/signin-complete.component';
import { SigninTestingComponent } from './app/authentication/signin-testing.component';
import { SigninComponent } from './app/authentication/signin.component';
import { AccountComponent } from './app/account/account.component';
import { CablesComponent } from './app/cables/cables.component';
import { unsavedChangesGuard } from './app/shared/guards/unsaved-changes.guard';
import { DashboardComponent } from './app/dashboard/dashboard.component';
import { ProjectsComponent } from './app/projects/projects.component';
import { HelpComponent } from './app/dashboard/help/help.component';
import { SettingsComponent } from './app/settings/settings.component';
import { SharedBillingUsersComponent } from './app/settings/shared-billing-users/shared-billing-users.component';

if (environment.production) {
  enableProdMode();
}

// ← DEBUG TOKEN INJECTION (for localhost only)
if (!environment.production && location.hostname === 'localhost') {
  (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = '9494524b-a712-480f-88da-704b296f0fb6';
  console.log('Firebase App Check: Debug token enabled for localhost.');
}

const routes: Routes = [
  { path: 'dashboard', component: DashboardComponent, canActivate: [authGuard] },
  { path: 'projects', component: ProjectsComponent, canActivate: [authGuard], canDeactivate: [unsavedChangesGuard] },
  { path: 'cables/:projectUid', component: CablesComponent, canActivate: [authGuard], canDeactivate: [unsavedChangesGuard] },
  { path: 'account', component: AccountComponent, canActivate: [authGuard] },
  { path: 'help', component: HelpComponent, canActivate: [authGuard] },
  { path: 'settings', component: SettingsComponent, canActivate: [authGuard] },
  { path: 'settings/shared-billing/users', component: SharedBillingUsersComponent, canActivate: [authGuard] },
  { path: 'administration', component: AdministrationComponent, canActivate: [authGuard] },
  { path: 'profile/edit', component: ProfileEditComponent, canActivate: [authGuard] },
  { path: 'authentication/signin', component: SigninComponent, canActivate: [authGuard] },
  { path: 'authentication/signin-complete', component: SigninCompleteComponent, canActivate: [authGuard] },
  { path: 'authentication/signin-testing', component: SigninTestingComponent, canActivate: [authGuard] },
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  { path: '**', redirectTo: 'dashboard' }
];

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),

    provideHttpClient(withXhr(), withInterceptorsFromDi()),

    provideFirebaseServices(environment.firebase, environment.reCaptchaV3Key),

    provideAppInitializer(() => {
      inject(FirebaseAnalyticsService).startTracking();
    }),

    // === Your API services & global providers ===
    MceApiService,
    { provide: API_BASE_URL, useValue: environment.mceApiUrl },

    // HTTP interceptor chain
    { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptor, multi: true },

    // Global error handler
    { provide: ErrorHandler, useClass: CustomErrorService },
  ],
})
  .catch(err => console.error('Bootstrap error:', err));
